import type { IngestRun } from '../api/types'
import { formatCount } from './format'
import { formatDate } from './time'

const KINDS: Record<string, string> = {
  schedule: 'Daily collection',
  reconcile: 'Weekly sweep',
  audit: 'Monthly audit',
  backfill: 'Backfill',
  manual: 'Manual',
}

/** `ingest_run.trigger` in the panel's words. Unknown kinds pass through. */
export function runKind(trigger: string): string {
  return KINDS[trigger] ?? trigger
}

const MINUTE = 60_000

/** The local days a run's window covers.
 *
 *  Ends are vendor-specific: MDS windows end at 23:59 inclusive, CMT Edge
 *  windows at the next midnight exclusive. Stepping back one minute from the
 *  end lands on the last day covered in both cases, so a CMT window is not
 *  shown reaching a day it never read. */
export function runWindow(run: IngestRun): string {
  if (!run.from_ts || !run.to_ts) return '—'
  const first = formatDate(run.from_ts)
  const last = formatDate(new Date(new Date(run.to_ts).getTime() - MINUTE))
  return first === last ? first : `${first} – ${last}`
}

/** What a run moved, in units that can be compared.
 *
 *  `rows_fetched` counts vendor rows — one per timestamp, each carrying every
 *  channel — while `rows_written` counts individual readings, so "fetched"
 *  next to "written" read as if a run wrote more than it received. For an
 *  audit the count is readings compared, and nothing is written. */
export function runCounts(run: IngestRun): string {
  if (run.trigger === 'audit') return `${formatCount(run.rows_fetched)} compared`
  return `${formatCount(run.rows_fetched)} vendor rows · `
    + `${formatCount(run.rows_written)} readings written`
}

/** Status is carried by the word; the pill only adds weight.
 *
 *  docs/DESIGN_GUIDELINE.md §2.6 forbids status colour from the blue tints,
 *  so `.pill.good` (a Hydro Blue fill) is deliberately unused; the approved
 *  status set went to station health (intake v2 phase 5), not to this list: these are the monochrome pills
 *  only, and a normal run gets none at all. */
export function statusPill(status: IngestRun['status']): 'bad' | 'warn' | 'neutral' | null {
  if (status === 'failed') return 'bad'
  if (status === 'partial') return 'warn'
  if (status === 'pending') return 'neutral'
  return null
}

/** The newest run per station, in the order given (newest first). */
export function latestPerStation(runs: IngestRun[]): IngestRun[] {
  const seen = new Set<string | null>()
  return runs.filter((run) => {
    if (seen.has(run.station)) return false
    seen.add(run.station)
    return true
  })
}
